<script setup>
import { ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import PipelineAutomationsFlowBuilder from './components/PipelineAutomationsFlowBuilder.vue';
import { usePipelinePermissions } from './composables/usePipelinePermissions';
import FlowBuilder from './automations/FlowBuilder.vue';
import TemplatesGallery from './automations/components/TemplatesGallery.vue';

const { t } = useI18n();
const { showAlert } = useAlert();
const route = useRoute();
const router = useRouter();
const { canManageAutomations } = usePipelinePermissions();

const currentView = ref('list');
const editingAutomationId = ref(null);
const initialTab = ref('canvas');
const showTemplatesGallery = ref(false);

const normalizePositiveAutomationId = value => {
  if (Array.isArray(value) || !['string', 'number'].includes(typeof value)) {
    return null;
  }
  const candidate = String(value);
  if (!/^[1-9]\d*$/.test(candidate)) return null;
  const numericId = Number(candidate);
  if (!Number.isSafeInteger(numericId)) return null;
  return typeof value === 'number' ? numericId : candidate;
};

const replaceViewQuery = query => {
  router.replace({
    query: {
      ...route.query,
      ...query,
    },
  });
};

const showEditor = (automationId = null, tab = 'canvas') => {
  const normalizedId =
    automationId === null ? null : normalizePositiveAutomationId(automationId);
  if (automationId !== null && normalizedId === null) {
    currentView.value = 'list';
    editingAutomationId.value = null;
    initialTab.value = 'canvas';
    replaceViewQuery({
      view: undefined,
      automation: undefined,
      tab: undefined,
    });
    return;
  }
  if (!canManageAutomations.value && !normalizedId) return;
  const allowedTab = canManageAutomations.value ? tab : 'history';
  editingAutomationId.value = normalizedId;
  initialTab.value = allowedTab;
  currentView.value = 'editor';
  replaceViewQuery({
    view: 'editor',
    automation: normalizedId || undefined,
    tab: allowedTab === 'history' ? 'history' : undefined,
  });
};

// Abre o editor diretamente na aba de histórico de execuções, sem o usuário
// precisar passar pelo canvas (pedido operacional: ver o que rodou/falhou).
const showHistory = automationId => showEditor(automationId, 'history');

const showList = () => {
  currentView.value = 'list';
  editingAutomationId.value = null;
  initialTab.value = 'canvas';
  replaceViewQuery({
    view: undefined,
    automation: undefined,
    tab: undefined,
  });
};

// Após save de automação, mantém o editor aberto na automação correta.
// Para nova automação, o backend retorna o id recém-criado — sem propagar,
// a próxima abertura precisaria ser feita pela lista (UX ruim e potencial
// fonte do bug "salvei e voltou em branco").
const handleSaved = savedId => {
  const normalizedId = normalizePositiveAutomationId(savedId);
  if (normalizedId) {
    editingAutomationId.value = normalizedId;
    replaceViewQuery({ automation: normalizedId });
  }
};

const handleTemplateUsed = automation => {
  showTemplatesGallery.value = false;
  showAlert(t('PIPELINE.FLOW_BUILDER.TEMPLATES_GALLERY.SUCCESS_TOAST'));
  showEditor(automation.id);
};

watch(
  () => [
    route.query.view,
    route.query.automation,
    route.query.tab,
    canManageAutomations.value,
  ],
  ([view, automationId, tab, userCanManageAutomations]) => {
    if (view !== 'editor') {
      currentView.value = 'list';
      editingAutomationId.value = null;
      initialTab.value = 'canvas';
      return;
    }

    const hasAutomationId = automationId !== undefined;
    const normalizedId = hasAutomationId
      ? normalizePositiveAutomationId(automationId)
      : null;
    if (hasAutomationId && normalizedId === null) {
      currentView.value = 'list';
      editingAutomationId.value = null;
      initialTab.value = 'canvas';
      replaceViewQuery({
        view: undefined,
        automation: undefined,
        tab: undefined,
      });
      return;
    }

    if (!userCanManageAutomations && !normalizedId) {
      currentView.value = 'list';
      editingAutomationId.value = null;
      initialTab.value = 'canvas';
      replaceViewQuery({
        view: undefined,
        automation: undefined,
        tab: undefined,
      });
      return;
    }

    currentView.value = 'editor';
    editingAutomationId.value = normalizedId;
    initialTab.value =
      !userCanManageAutomations || (tab === 'history' && normalizedId)
        ? 'history'
        : 'canvas';

    if (!userCanManageAutomations && tab !== 'history') {
      replaceViewQuery({ tab: 'history' });
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col h-full w-full">
    <FlowBuilder
      v-if="currentView === 'editor'"
      :key="`${editingAutomationId || 'new'}:${initialTab}`"
      :automation-id="editingAutomationId"
      :initial-tab="initialTab"
      :readonly="!canManageAutomations"
      @close="showList"
      @save="handleSaved"
    />
    <PipelineAutomationsFlowBuilder
      v-else
      @create="showEditor(null)"
      @edit="showEditor($event)"
      @history="showHistory($event)"
      @templates="showTemplatesGallery = true"
    />

    <!-- Templates Gallery Modal -->
    <TemplatesGallery
      v-if="showTemplatesGallery && canManageAutomations"
      @close="showTemplatesGallery = false"
      @template-used="handleTemplateUsed"
    />
  </div>
</template>
